'use strict';

require('isomorphic-fetch');
const qs = require('qs');
const gaTracking = require('dibs-tracking');

const querystring = require('querystring');
const tokenReplace = require('dibs-token-replace');

const getSocialWindowPosition = require('../../../social/helpers/getSocialWindowPosition');
const PIN_COUNT = 'https://api.pinterest.com/v1/urls/count.json';
const PINTEREST_SCOPE = 'read_public,write_public';

const ModifySocialNetworkConnectionMutation = require('../../components/SocialNetworks/ModifySocialNetworkConnectionMutation.es')
    .default;

const User = require('../../models/User');
const { localStorage } = require('dibs-browser-storage');
const LOCAL_STORAGE_NAMESPACE = 'pinterest';
const LOCAL_STORAGE_MODAL = 'connectAccountsModal';
const IMPRESSION_COUNT = 3;

const getPinterestLocalStorageData = () => {
    return localStorage.getItem(LOCAL_STORAGE_NAMESPACE) || {};
};

const setPinterestLocalStorageData = data => {
    const pinterest = getPinterestLocalStorageData();
    localStorage.setItem(LOCAL_STORAGE_NAMESPACE, Object.assign(pinterest, data));
};

const canShowConnectAccountPromotion = ({ namespace, socialNetwork, userSocialNetwork }) => {
    if (!User.loggedIn()) {
        return false;
    }

    // Pinterest is not accessible, or pinterest is already connected
    if (!socialNetwork || userSocialNetwork) {
        return false;
    }

    // If namespace was not passed, then we do not need to check
    // impression count - resolving promise.
    if (!namespace) {
        return true;
    }

    let impressionCount = getPinterestLocalStorageData()[namespace];

    if (!impressionCount) {
        impressionCount = 0;
        setPinterestLocalStorageData({
            [namespace]: impressionCount,
        });
        return true;
    }

    if (impressionCount < IMPRESSION_COUNT) {
        return true;
    } else {
        return false;
    }
};

const canShowConnectAccountsModal = payload => {
    return canShowConnectAccountPromotion({
        ...payload,
        namespace: LOCAL_STORAGE_MODAL,
    });
};

const increaseConnectAccountsModalShowCount = () => {
    let { connectAccountsModal } = getPinterestLocalStorageData();
    connectAccountsModal += 1;
    setPinterestLocalStorageData({ connectAccountsModal });
};

function parseResponseBody(response) {
    const reader = response.body.getReader();
    const decoder = new TextDecoder();
    let decodedResult = '';

    function parse() {
        return reader.read().then(result => {
            if (!result.done) {
                decodedResult += decoder.decode(result.value || new Uint8Array(), {
                    stream: !result.done,
                });

                return parse();
            }

            return decodedResult;
        });
    }

    return parse();
}

function listenWindowClose(pinterestWindow) {
    return new Promise(resolve => {
        const interval = setInterval(() => {
            try {
                if (pinterestWindow === null || pinterestWindow.closed) {
                    clearInterval(interval);
                    resolve();
                }
            } catch (e) {
                resolve();
            }
        }, 500);
    });
}

function login(payload = {}) {
    // moved it inside the method call so `window` is accessed on demand instead of on page load
    const OAUTH_REDIRECT = window.location.origin;
    const { pinterestWindow, clientId } = payload;
    const loginUrl = `https://api.pinterest.com/oauth/?client_id=${clientId}&redirect_uri=${
        OAUTH_REDIRECT
    }&scope=${PINTEREST_SCOPE}&redirect_type=js&response_type=token`;
    pinterestWindow.location = loginUrl;

    return new Promise((resolve, reject) => {
        const listener = event => {
            const { origin, data } = event;
            if (origin === 'https://api.pinterest.com') {
                if (data && data.access_token) {
                    resolve({
                        accessToken: data.access_token,
                        scope: PINTEREST_SCOPE,
                    });
                } else {
                    reject();
                }

                window.removeEventListener('message', listener);
                pinterestWindow.close();
            }
        };

        window.addEventListener('message', listener);

        listenWindowClose(pinterestWindow).then(() => {
            window.removeEventListener('message', listener);
            reject();
        });
    });
}

function getPinCount(payload = {}) {
    const date = new Date();
    const timestamp = date.getTime();
    const { url } = payload;

    return fetch(`${PIN_COUNT}?callback=whatever&${qs.stringify({ url, timestamp })}`)
        .then(response => {
            if (response.status === 200) {
                return parseResponseBody(response);
            }

            return new Error('Unable to load pinterest pin count');
        })
        .then(response => {
            try {
                response = JSON.parse(response.match(/\((.*?)\)/)[1]);
            } catch (error) {
                console.warn(error);
            }

            return response.count;
        });
}

const pinterest = {
    canShowConnectAccountPromotion,

    openWindow() {
        const width = 665;
        const height = 500;
        const windowOptions = {
            scrollbars: 'yes',
            resizable: 'no',
            toolbar: 'no',
            height: height,
            width: width,
            top: 0,
            left: 0,
        };

        Object.assign(windowOptions, getSocialWindowPosition(height, width));
        return window.open('', 'pinterest', querystring.stringify(windowOptions, ','));
    },

    connectAccounts(payload) {
        const { relay, clientId, userId, pinterestWindow, tracking, onSuccess } = payload;

        login({ pinterestWindow, clientId })
            .then(
                session =>
                    new Promise((resolve, reject) => {
                        ModifySocialNetworkConnectionMutation.commit(relay.environment, {
                            clientId,
                            userId,
                            action: 'CONNECT',
                            socialNetworkName: 'PINTEREST',
                            accessToken: session.accessToken,
                            onSuccess: () => {
                                onSuccess();
                                resolve();
                            },
                            onFailure: reject,
                        });
                    })
            )
            .then(() => {
                pinterest.track(tracking);
            });
    },

    showConnectModalAfterPinAdded(payload) {
        const { url, tracking, onSuccess } = payload;
        let pinCount = 0;

        if (canShowConnectAccountsModal(payload)) {
            getPinCount({ url })
                .then(count => {
                    pinCount = count;
                    return pinterest.pin(payload);
                })
                .then(() => getPinCount({ url }))
                .then(count => {
                    if (pinCount !== count) {
                        increaseConnectAccountsModalShowCount();
                        pinterest.track(tracking);
                        onSuccess();
                    }
                });
        } else {
            pinterest.pin(payload);
        }
    },

    pin(payload = {}) {
        const PINTEREST_URL =
            'https://pinterest.com/pin/create/button/?url={url}&media={media}&description={description}';
        const { pinterestWindow, url, media, description } = payload;

        pinterestWindow.location = tokenReplace(PINTEREST_URL, { url, media, description });
        return listenWindowClose(pinterestWindow);
    },

    track(payload) {
        const { category, action, label } = payload;

        if (category && action && label) {
            gaTracking.trackEvent({ category, action, label });
        }
    },
};

module.exports = pinterest;



// WEBPACK FOOTER //
// ./assets/js/global/utils/social/pinterest.es.js